// src/pages/LocationMaster.js

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader,
  AlertTriangle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import Navbar from '../components/navbar';
import { getAuthHeaders } from '../hydraApiService'; // ← ADDED

const API_BASE = 'https://dpp-api.acespireconsulting.com/api';
const ITEMS_PER_PAGE = 30;

// ── Pagination Controls ───────────────────────────────────────────────────────
function PaginationControls({ currentPage, totalPages, total, itemsPerPage, onPageChange }) {
  const start = (currentPage - 1) * itemsPerPage + 1;
  const end = Math.min(currentPage * itemsPerPage, total);

  return (
    <div className="bg-white border-t border-gray-200 px-4 py-3 sm:px-6">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="text-sm text-gray-700">
          Showing <span className="font-medium">{start}</span> to{' '}
          <span className="font-medium">{end}</span> of{' '}
          <span className="font-medium">{total}</span> records
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1.5 rounded-lg flex items-center gap-1 text-sm font-medium transition-colors ${
              currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <div className="px-3 py-1.5 text-sm font-medium text-gray-700">
            Page {currentPage} of {totalPages}
          </div>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1.5 rounded-lg flex items-center gap-1 text-sm font-medium transition-colors ${
              currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Mobile Card ───────────────────────────────────────────────────────────────
function LocationCard({ loc }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
      <div className="space-y-3">
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Location ID</p>
          <p className="text-sm font-semibold text-gray-900 break-all">{loc.locationId || 'N/A'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Facility Name</p>
          <p className="text-sm font-mono text-gray-700">{loc.facilityName || 'N/A'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Organization</p>
          <p className="text-sm text-gray-700">{loc.organizationName || '—'}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Type</p>
            <p className="text-sm text-gray-700">{loc.locationType || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Status</p>
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
              loc.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
            }`}>
              {loc.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">City</p>
            <p className="text-sm text-gray-700">{loc.addressCity || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">State</p>
            <p className="text-sm text-gray-700">{loc.addressState || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Country</p>
            <p className="text-sm text-gray-700">{loc.addressCountry || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Postal Code</p>
            <p className="text-sm text-gray-700">{loc.addressPostalCode || '—'}</p>
          </div>
        </div>
        {/* {loc.contactEmail && (
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Email</p>
            <p className="text-sm text-gray-700">{loc.contactEmail}</p>
          </div>
        )} */}
        {/* {loc.contactPhone && (
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Phone</p>
            <p className="text-sm text-gray-700">{loc.contactPhone}</p>
          </div>
        )} */}
        {loc.authorizedEventTypes?.length > 0 && (
          <div>
            <p className="text-xs text-gray-500 mb-1">Authorized Event Types</p>
            <div className="flex flex-wrap gap-1">
              {loc.authorizedEventTypes.map((e, i) => (
                <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">{e}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
function LocationMaster() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [showSpinner, setShowSpinner] = useState(false);

  const [filters, setFilters] = useState({ locationType: '', isActive: '', city: '', country: '' });
  const [appliedFilters, setAppliedFilters] = useState({ locationType: '', isActive: '', city: '', country: '' });

  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, hasMore: false });

  const buildQueryString = (page, f) => {
    const offset = (page - 1) * ITEMS_PER_PAGE;
    const params = new URLSearchParams({ limit: ITEMS_PER_PAGE, offset });
    if (f.locationType) params.append('locationType', f.locationType);
    if (f.isActive !== '') params.append('isActive', f.isActive);
    if (f.city) params.append('city', f.city);
    if (f.country) params.append('country', f.country);
    return params.toString();
  };

  const fetchLocations = async (page, f) => {
    console.log('authToken:', localStorage.getItem('authToken'));
  console.log('access_token:', localStorage.getItem('access_token'));
  console.log('headers:', getAuthHeaders());
    let spinnerTimer = null;
    try {
      setLoading(true);
      setError(null);
      spinnerTimer = setTimeout(() => setShowSpinner(true), 900);

      const qs = buildQueryString(page, f);
      const response = await fetch(`${API_BASE}/locations?${qs}`, {
        headers: getAuthHeaders(), // ← CHANGED: was { 'Content-Type': 'application/json' }
      });

      if (response.status === 401) { // ← ADDED
        navigate('/login');
        return;
      }

      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
      const data = await response.json();

      if (data.success && data.data?.locations) {
        setLocations(data.data.locations);
        const p = data.data.pagination;
        setPagination({ total: p.total, totalPages: p.totalPages, hasMore: p.hasMore });
      } else {
        throw new Error('Invalid response format');
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Failed to fetch location data');
    } finally {
      if (spinnerTimer) clearTimeout(spinnerTimer);
      setShowSpinner(false);
      setLoading(false);
    }
  };

  // Fetch when page changes
  useEffect(() => {
    fetchLocations(currentPage, appliedFilters);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleApplyFilters = () => {
    const f = { ...filters };
    setAppliedFilters(f);
    setCurrentPage(1);
    fetchLocations(1, f);
  };

  const handleClearFilters = () => {
    const empty = { locationType: '', isActive: '', city: '', country: '' };
    setFilters(empty);
    setAppliedFilters(empty);
    setCurrentPage(1);
    fetchLocations(1, empty);
  };

  // ── Loading state ───────────────────────────────────────────────────────────
  if (showSpinner && currentPage === 1) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading location master data...</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Error state ─────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center max-w-md mx-auto p-6">
            <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Data</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => fetchLocations(currentPage, appliedFilters)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
      <Navbar />

      {/* Loading overlay for page changes */}
      {showSpinner && currentPage > 1 && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3">
            <Loader className="w-6 h-6 text-blue-600 animate-spin" />
            <p className="text-gray-700">Loading page {currentPage}...</p>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-xl overflow-hidden shadow">
        <button
          onClick={() => navigate('/authenticator')}
          className="flex items-center text-blue-600 hover:text-blue-700 transition-colors text-sm sm:text-base"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
          Back
        </button>
        <h2 className="text-blue-600 font-medium text-sm sm:text-base">
          Location Master ({pagination.total} records)
        </h2>
      </div>

      {/* Filters */}
      {/* <div className="mx-2 sm:mx-4 mt-4 bg-white rounded-xl shadow p-4">
        ... (unchanged, kept commented out)
      </div> */}

      {/* Content */}
      <div className="p-4 sm:p-6 lg:p-8">

        {/* Desktop Table */}
        <div className="hidden lg:block bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ backgroundColor: '#D6E1F9' }}>
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Location ID</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Facility Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Organization</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">City</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">State</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Country</th>
                  {/* <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Contact Email</th> */}
                  {/* <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Contact Phone</th> */}
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((loc, index) => (
                  <tr key={loc.locationId || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">{loc.locationId || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm font-mono text-gray-600 max-w-[200px] truncate" title={loc.facilityName}>{loc.facilityName || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 max-w-[160px] truncate" title={loc.organizationName}>{loc.organizationName || '—'}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{loc.locationType || '—'}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{loc.addressCity || '—'}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{loc.addressState || '—'}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{loc.addressCountry || '—'}</td>
                    {/* <td className="px-4 py-4 text-sm text-gray-700">{loc.contactEmail || '—'}</td> */}
                    {/* <td className="px-4 py-4 text-sm text-gray-700">{loc.contactPhone || '—'}</td> */}
                    <td className="px-4 py-4 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        loc.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                      }`}>
                        {loc.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls
            currentPage={currentPage}
            totalPages={pagination.totalPages}
            total={pagination.total}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
        </div>

        {/* Tablet Table */}
        <div className="hidden md:block lg:hidden bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead style={{ backgroundColor: '#D6E1F9' }}>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Location ID</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Facility Name</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">City</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Country</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((loc, index) => (
                  <tr key={loc.locationId || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-3 text-xs font-medium text-gray-900">{loc.locationId || 'N/A'}</td>
                    <td className="px-3 py-3 text-xs font-mono text-gray-600 max-w-[180px] truncate">{loc.facilityName || 'N/A'}</td>
                    <td className="px-3 py-3 text-xs text-gray-700">{loc.locationType || '—'}</td>
                    <td className="px-3 py-3 text-xs text-gray-700">{loc.addressCity || '—'}</td>
                    <td className="px-3 py-3 text-xs text-gray-700">{loc.addressCountry || '—'}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        loc.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                      }`}>
                        {loc.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 text-center border-t">
            Scroll horizontally to see more columns →
          </div>
          <PaginationControls
            currentPage={currentPage}
            totalPages={pagination.totalPages}
            total={pagination.total}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden">
          {locations.map((loc, index) => (
            <LocationCard key={loc.locationId || index} loc={loc} />
          ))}
          <div className="bg-white rounded-lg shadow-sm mt-4">
            <PaginationControls
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              total={pagination.total}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={handlePageChange}
            />
          </div>
        </div>

        {/* Empty State */}
        {locations.length === 0 && !loading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No location data found</h3>
            <p className="text-gray-600">Try adjusting your filters or upload a CSV file to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default LocationMaster;